import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../axios/axiosInstance';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input, message, Popconfirm, Select, Table } from 'antd';
import { useMasterGradesSelector, useMasterSubjectsSelector } from '../../../../redux/selector';
import { ColumnsType } from 'antd/es/table/interface';
import { ModePage } from 'common/enum';
import AddNewLectureModal from './AddNewLectureModal';

import processError from '../../../../utils/processError';
import { Lecture } from '../../../../model/response';
import { LectureStatus, LectureType } from '../../../../model/enum';
import { lectureStatusList, lectureTypeList } from '../../../../utils/enumMapper';
import WebViewActiveCheckbox from '../../../common/WebViewActiveCheckbox';
import { toggleLectureWebviewActive } from '../../../../service/lectureService';
import useTableConfig from '../../../../hooks/useTableConfig';

const { Option } = Select;

export default function LectureManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig();
  const grades = useMasterGradesSelector();
  const subjects = useMasterSubjectsSelector();
  const [keyword, setKeyword] = useState<string>('');
  const [gradeFilter, setGradeFilter] = useState<string>('');
  const [lectureId, setLectureId] = useState<string>('');
  const [subjectId, setSubjectId] = useState<string>('');
  const [modeEdit, setModeEdit] = useState<ModePage>(ModePage.ADD);
  const rootElement: any = useRef(null);
  const [isOpen, setOpenModal] = useState<boolean>(false);

  const getLectureTypeLabel = (value: LectureType): string => {
    const type = lectureTypeList.find((type) => type.value === value);
    return type ? type.label : '';
  };

  const getLectureStatusLabel = (value: LectureStatus): string => {
    const status = lectureStatusList.find((status) => status.value === value);
    return status ? status.label : '';
  };

  const [data, setData] = useState<Lecture[]>();
  const [loading, setLoading] = useState(false);

  const getSubjectName = useCallback(
    (subjectId: string | undefined) => {
      if (!subjectId) return '';
      return subjects.find((subject) => subject.id === subjectId)?.name || '';
    },
    [subjects],
  );

  const getGradeName = useCallback(
    (gradeId: string | undefined) => {
      if (!gradeId) return '';
      return grades.find((grade) => grade.id === gradeId)?.name || '';
    },
    [grades],
  );

  const columns: ColumnsType<Lecture> = [
    {
      title: 'Tên',
      dataIndex: 'name',
      width: '30%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('name'),
    },
    {
      title: 'Lớp',
      dataIndex: 'grade.name',
      render: (_, record) => getGradeName(record.grade?.id),
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('grade.name'),
    },
    {
      title: 'Môn học',
      dataIndex: 'subject',
      render: (_, record) => getSubjectName(record.subject?.id),
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('subject.name'),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      render: (_, record) => getLectureTypeLabel(record.type),
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('type'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      render: (_, record) => getLectureStatusLabel(record.status),
      width: '15%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('status'),
    },
    {
      title: 'Hiển thị',
      dataIndex: 'webViewActive',
      align: 'center',
      render: (_, record) => (
        <WebViewActiveCheckbox
          currentActiveValue={record.webViewActive}
          itemName={'bài giảng'}
          setLoading={setLoading}
          toggleWebviewActive={async () => toggleLectureWebviewActive(record.id)}
          reloadPageData={loadListTheories}
        />
      ),
    },
    {
      title: 'Thứ tự',
      dataIndex: 'webViewOrder',
      align: 'center',
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined />
          </button>
          <Popconfirm
            title="Xóa bài giảng"
            description="Xác nhận xóa bài giảng. Hành động này không thể hoàn tác?"
            onConfirm={() => deleteLecture(record)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const deleteLecture = async (record: Lecture) => {
    setLoading(true);

    try {
      await axiosInstance({
        url: `/lectures/${record.id}`,
        method: 'delete',
      });
      message.success('Deleted successfully.');
      await loadListTheories();
    } catch (e) {
      processError(e);
    }
    setLoading(false);
  };

  const moveEditView = async (e: any, record: Lecture) => {
    setModeEdit(ModePage.EDIT);
    setOpenModal(true);
    setLectureId(record.id);
  };

  const loadListTheories = async () => {
    setLoading(true);
    const res = await axiosInstance({
      url: '/lectures/page',
      params: {
        keyword,
        size: pageSize,
        page: currentPage,
        sortBy,
        direction,
        subjectId: subjectId,
        gradeId: gradeFilter,
        fromCMS: true,
      },
    });

    if (res.status === 200) {
      setData(res.data.content);
      setLoading(false);
      setTotalElements(res.data.totalElements);
    }
  };

  const onChangeSelect = (e: any, fieldName: string) => {
    if (fieldName === 'grade') {
      setGradeFilter(e);
    } else if (fieldName === 'subject') {
      setSubjectId(e);
    }
  };

  const onSearch = (e: any) => {
    setKeyword(e.target.value);
  };

  const addNewLecture = () => {
    setModeEdit(ModePage.ADD);
    setOpenModal(true);
  };

  const onCloseModal = () => {
    setOpenModal(false);
  };

  const onSave = () => {
    setOpenModal(false);
    loadListTheories().then();
  };

  useEffect(() => {
    loadListTheories().then();
  }, [currentPage, pageSize, sortBy, direction, gradeFilter, subjectId, keyword]);

  return (
    <div className="w-full h-full p-4" ref={rootElement}>
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={addNewLecture}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
          <div className="min-w-[170px] max-w-[170px] flex items-center gap-2">
            <div>Lớp</div>
            <Select
              onChange={(e) => onChangeSelect(e, 'grade')}
              className="w-full"
              placeholder="Lớp - Tất cả"
              style={{ flex: 1 }}
              value={gradeFilter}
              options={[
                { value: '', label: 'Lớp - Tất cả' },
                ...grades.map((grade) => ({ value: grade.id, label: grade.name })),
              ]}
            />
          </div>
          <div className="min-w-[170px] max-w-[170px] flex items-center gap-2">
            <Select
              id="subject"
              placeholder="Môn - Tất cả"
              className="min-w-40"
              onChange={(e) => onChangeSelect(e, 'subject')}
            >
              <Option value="">Môn - Tất cả</Option>
              {subjects?.map((subject) => <Option value={subject.id}>{subject?.name}</Option>)}
            </Select>
          </div>
        </div>
        <Input placeholder="Search..." name="keyword" className="w-fit" onPressEnter={onSearch} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          scroll={{ y: (window.innerHeight * 52) / 100 }}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <AddNewLectureModal
        isOpen={isOpen}
        onCloseModal={onCloseModal}
        modePage={modeEdit}
        onSave={onSave}
        lectureId={lectureId}
      />
    </div>
  );
}
